import { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import API_ROUTES_V2 from "~/api-routes-v2";
import Categories from "~/components/Processes/conversations/utils/categories.json";
import apiService from "~/services/service";
import { downloadFileHandler, openPreviewInNewTab } from "~/utils/fileUtils";
import useDateTime from "~/hooks/useDateTime";

function getSubCategoryCode(
  subCategory: string | { code?: string; name?: string } | null | undefined,
): string {
  if (subCategory == null || subCategory === "") return "";
  if (typeof subCategory === "object" && subCategory?.code != null) {
    return String(subCategory.code);
  }
  return String(subCategory);
}

type UseDocumentDescriptionArgs = {
  data: any;
  id: string;
  type: string;
  visible?: boolean;
  onVisibleChange?: (v: boolean) => void;
  onDescriptionUpdated?: (row: any, newDesc?: string) => void;
  onDataUpdate?: (updatedRow: any) => void;
};

function useDocumentDescription({
  data,
  id,
  type,
  visible,
  onVisibleChange,
  onDescriptionUpdated,
  onDataUpdate,
}: UseDocumentDescriptionArgs) {
  const { formatDateTime } = useDateTime();
  const { role } = useSelector((state: any) => state.login);

  const [open, setOpen] = useState(false);
  const [description, setDescription] = useState("");
  const [isUpdated, setIsUpdated] = useState(false);
  const [restoreMessage, setRestoreMessage] = useState("");
  const [descriptionSuccessMessage, setDescriptionSuccessMessage] =
    useState("");
  const [metadataErrorMessage, setMetadataErrorMessage] = useState("");
  const [hasLocalDescriptionOverride, setHasLocalDescriptionOverride] =
    useState(false);

  const [isEditMode, setIsEditMode] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [fileCategory, setFileCategory] = useState(data?.category ?? "");
  const [fileSubCategory, setFileSubCategory] = useState(() =>
    getSubCategoryCode(data?.subCategory),
  );
  const [fileYear, setFileYear] = useState(
    (data as any)?.year || (data as any)?.fileYear || "",
  );
  const [fileMonth, setFileMonth] = useState(
    (data as any)?.month || (data as any)?.fileMonth || "",
  );
  const [fileMonthYear, setFileMonthYear] = useState<Date | null>(null);
  const [lastModified, setLastModified] = useState(
    (data as any)?.updatedAt || data?.createdAt || "",
  );
  const [saving, setSaving] = useState(false);
  const [categorySaving, setCategorySaving] = useState(false);
  const [dateSaving, setDateSaving] = useState(false);
  const [showCategories, setShowCategories] = useState(false);
  const [showSubcategories, setShowSubcategories] = useState(false);
  const [filteredCategories, setFilteredCategories] = useState(
    Categories ?? [],
  );

  const categoryAccordionRef = useRef<HTMLDivElement>(null);
  const containerName = useMemo(() => {
    if (data?.containerName) return data.containerName;
    try {
      const url = new URL(data?.blob_url);
      return url.pathname.split("/")[1] || "documents";
    } catch {
      return "documents";
    }
  }, [data?.containerName, data?.blob_url]);

  const isGxmAdmin = role === "GXM_ADMIN";
  const canEdit =
    isGxmAdmin || (role != null && String(role).startsWith("GXM"));

  const isControlled = visible !== undefined && onVisibleChange != null;
  const sidebarVisible = isControlled ? visible : open;
  const setSidebarVisible = isControlled
    ? (v: boolean) => onVisibleChange?.(v)
    : setOpen;

  const safeDecode = (text: string) => {
    try {
      return decodeURIComponent(text || "");
    } catch {
      return text || "";
    }
  };

  const truncateText = (text: string, maxLength = 40) => {
    if (!text) return "-";
    return text.length > maxLength
      ? text.substring(0, maxLength) + "..."
      : text;
  };

  const formatDate = (dateStr: string) =>
    dateStr
      ? formatDateTime({
          date: new Date(dateStr),
          dateformat: "MMMM do, yyyy, h:mm a",
        })
      : "-";

  const months = useMemo(
    () => [
      "January",
      "February",
      "March",
      "April",
      "May",
      "June",
      "July",
      "August",
      "September",
      "October",
      "November",
      "December",
    ],
    [],
  );

  const categoryOptions = useMemo(() => {
    const list = (Categories ?? []) as Array<any>;
    const isEmployeeInfoSelected = fileCategory === "EMPLOYEE_INFORMATION";

    return list.filter((c) => {
      if (
        String(c?.name ?? "")
          .trim()
          .toLowerCase() !== "personal documents"
      ) {
        return true;
      }
      return isEmployeeInfoSelected
        ? c?.code === "EMPLOYEE_INFORMATION"
        : c?.code !== "EMPLOYEE_INFORMATION";
    });
  }, [fileCategory]);

  const getMonthIndex = (value: any) => {
    if (!value) return null;
    const str = String(value).trim();
    const idx = months.findIndex((m) => m.toLowerCase() === str.toLowerCase());
    if (idx >= 0) return idx;

    const numeric = Number(str);
    if (!Number.isNaN(numeric) && numeric >= 1 && numeric <= 12) {
      return numeric - 1;
    }
    return null;
  };

  const computeMonthYearDate = (monthVal: any, yearVal: any) => {
    const monthIdx = getMonthIndex(monthVal);
    const y = Number(yearVal);
    if (monthIdx == null || Number.isNaN(y) || y <= 0) return null;
    return new Date(y, monthIdx, 1);
  };

  const handleMonthYearChange = (date: Date | null) => {
    setFileMonthYear(date);
    if (!date) {
      setFileMonth("");
      setFileYear("");
      return;
    }
    setFileMonth(String(date.getMonth() + 1).padStart(2, "0"));
    setFileYear(String(date.getFullYear()));
  };

  const yearOptions = useMemo(() => {
    const currentYear = new Date().getFullYear();
    return Array.from({ length: 16 }, (_, i) => currentYear - 10 + i);
  }, []);

  const subCategoryOptions = useMemo(() => {
    if (!fileCategory) return [];
    const cat = Categories?.find(
      (c: { code: string }) => c.code === fileCategory,
    );
    return (cat?.subCategory ?? []).map(
      (sub: { code: string; name: string }) => ({
        value: sub.code,
        label: sub.name,
      }),
    );
  }, [fileCategory]);

  const categoryName = Categories?.find(
    (c: { code: string }) => c.code === fileCategory,
  )?.name;
  const subCategoryName = subCategoryOptions.find(
    (s: { value: string }) => s.value === fileSubCategory,
  )?.label;

  const selectedCategory = useMemo(
    () =>
      Categories?.find((c: { code: string }) => c.code === fileCategory) ??
      null,
    [fileCategory],
  );

  const originalDescription = safeDecode(data?.description || "") || "";
  const originalCategory = data?.category ?? "";
  const originalYear = (data as any)?.year || (data as any)?.fileYear || "";
  const originalMonth = (data as any)?.month || (data as any)?.fileMonth || "";

  const [baselineCategory, setBaselineCategory] = useState(originalCategory);
  const [baselineSubCategory, setBaselineSubCategory] = useState(() =>
    getSubCategoryCode(data?.subCategory),
  );
  const [baselineYear, setBaselineYear] = useState(originalYear);
  const [baselineMonth, setBaselineMonth] = useState(originalMonth);

  const hasDescriptionChange =
    safeDecode(description || "") !== originalDescription;
  const hasMetadataChange =
    fileCategory !== baselineCategory ||
    fileSubCategory !== baselineSubCategory ||
    String(fileYear || "") !== String(baselineYear || "") ||
    String(fileMonth || "") !== String(baselineMonth || "");
  const hasChanges = hasDescriptionChange || hasMetadataChange;

  const handleUpdateDescription = async (blobName: string, desc: string) => {
    try {
      setMetadataErrorMessage("");
      await apiService(
        API_ROUTES_V2.UPDATE_DESCRIPTION,
        data?.description ? "PATCH" : isUpdated ? "PATCH" : "POST",
        {
          body: {
            containerName,
            blobName,
            description: encodeURIComponent(desc),
          },
          replace: { id, type },
        },
      );
      setIsUpdated(true);
      setRestoreMessage("Good Job! Description updated.");
      setDescriptionSuccessMessage("Good Job! Description updated.");
    } catch (err) {
      console.log("Error updating description", err);
      setMetadataErrorMessage("Oops! Something went wrong saving your update.");
      throw err;
    }
  };

  const searchCategory = (value: string) => {
    if (!value) setFilteredCategories(categoryOptions);
    else {
      const filtered = categoryOptions.filter((cat: { name?: string }) =>
        cat?.name?.toLowerCase().includes(value.toLowerCase()),
      );
      setFilteredCategories(filtered);
    }
  };

  const handleCategorySelect = (cat: { code: string; name: string }) => {
    setFileCategory(cat.code);
    setFileSubCategory("");
    setShowSubcategories(true);
  };

  const handleSubCategorySelect = (sub: { code: string; name: string }) => {
    setFileSubCategory(sub.code);
    setShowCategories(false);
    setShowSubcategories(false);
    setFilteredCategories(categoryOptions);
  };

  const handleDescriptionSave = async () => {
    if (!data?.blobName || !canEdit || !hasDescriptionChange) return;

    const descToSave = (description ?? "").trim();

    setSaving(true);
    setMetadataErrorMessage("");
    try {
      await handleUpdateDescription(data.blobName, descToSave);
      const updatedAt = new Date().toISOString();
      setLastModified(updatedAt);
      onDescriptionUpdated?.(
        { ...data, lastModified: updatedAt, updatedAt },
        descToSave,
      );
      onDataUpdate?.({
        ...data,
        description: encodeURIComponent(descToSave),
        lastModified: updatedAt,
        updatedAt,
      });
      setIsEditMode(false);
      setHasLocalDescriptionOverride(true);
    } catch (err) {
      console.error("Error saving description", err);
      if (descToSave === "") {
        setMetadataErrorMessage("");
        setDescriptionSuccessMessage("Good Job! Description updated.");
        setDescription("");
        setIsUpdated(true);
        const updatedAt = new Date().toISOString();
        setLastModified(updatedAt);
        onDescriptionUpdated?.(
          { ...data, description: "", lastModified: updatedAt, updatedAt },
          "",
        );
        onDataUpdate?.({
          ...data,
          description: "",
          lastModified: updatedAt,
          updatedAt,
        });
        setIsEditMode(false);
        setHasLocalDescriptionOverride(true);
      }
    } finally {
      setSaving(false);
    }
  };

  const handleMetadataSave = async (saveType?: "category" | "date") => {
    if (!data?.blobName || !canEdit || !hasMetadataChange) return;
    if (saveType === "category") setCategorySaving(true);
    if (saveType === "date") setDateSaving(true);
    setMetadataErrorMessage("");
    try {
      // GXM-2031: Always include category/subCategory in metadata to satisfy backend validation
      // Backend requires at least one metadata key, even if empty
      const metadataPayload: Record<string, any> = {
        category: fileCategory || "",
        subCategory: fileSubCategory || "",
      };
      if (fileYear) metadataPayload.year = fileYear;
      if (fileMonth) metadataPayload.month = fileMonth;

      await apiService(API_ROUTES_V2.UPDATE_ATTACHMENT_TAGS, "POST", {
        body: {
          containerName,
          blobName: data.blobName,
          tags: {
            category: fileCategory || "",
            subCategory: fileSubCategory || "",
          },
          metadata: metadataPayload,
          fileYear: String(fileYear) || undefined,
          fileMonth: String(fileMonth) || undefined,
          relatedObjectId: id,
          relatedObjectType: type,
        },
      });
      const updatedAt = new Date().toISOString();
      setLastModified(updatedAt);
      onDescriptionUpdated?.({
        ...data,
        category: fileCategory,
        subCategory: fileSubCategory,
        year: fileYear,
        month: fileMonth,
        period:
          fileYear && fileMonth
            ? `${fileYear}-${String(fileMonth).padStart(2, "0")}`
            : (data as any)?.period,
        lastModified: updatedAt,
        updatedAt: updatedAt,
      });

      setFileMonthYear(computeMonthYearDate(fileMonth, fileYear));
      setBaselineCategory(fileCategory);
      setBaselineSubCategory(fileSubCategory);
      setBaselineYear(fileYear);
      setBaselineMonth(fileMonth);
    } catch (err) {
      console.error("Error saving file details", err);
      setMetadataErrorMessage("Oops! Something went wrong saving your update.");
      setFileCategory(baselineCategory);
      setFileSubCategory(baselineSubCategory);
      setFileYear(baselineYear);
      setFileMonth(baselineMonth);
      setFileMonthYear(computeMonthYearDate(baselineMonth, baselineYear));
    } finally {
      if (saveType === "category") setCategorySaving(false);
      if (saveType === "date") setDateSaving(false);
    }
  };

  const handleDescriptionDiscard = () => {
    setDescription(originalDescription);
    setIsEditMode(false);
  };

  const handleMetadataDiscard = () => {
    setFileCategory(baselineCategory);
    setFileSubCategory(baselineSubCategory);
    setFileYear(baselineYear);
    setFileMonth(baselineMonth);
    setFileMonthYear(computeMonthYearDate(baselineMonth, baselineYear));
  };

  const getContainerName = (blobUrl?: string) =>
    blobUrl && blobUrl.includes("/ess") ? "ess" : containerName;

  const handleViewInBrowser = () => {
    if (!data?.blob_url) return;
    openPreviewInNewTab(
      { objectURL: data.blob_url },
      getContainerName(data.blob_url as string),
    );
  };

  const handleDownloadFile = () => {
    if (!data?.blob_url) return;
    downloadFileHandler(data, getContainerName(data.blob_url as string));
  };

  const openEditMode = () => {
    setIsEditMode(true);
    setRestoreMessage("");
    setDescriptionSuccessMessage("");
  };

  useEffect(() => {
    const nextDesc = data?.description ?? "";
    const nextCategory = data?.category ?? "";
    const nextSubCategory = getSubCategoryCode(data?.subCategory);

    let nextYear = (data as any)?.year || (data as any)?.fileYear || "";
    let nextMonth = (data as any)?.month || (data as any)?.fileMonth || "";

    const period = (data as any)?.period;
    if ((!nextYear || !nextMonth) && period && period.includes("-")) {
      const [year, month] = period.split("-");
      nextYear = year;
      nextMonth = month;
    }

    if (!hasLocalDescriptionOverride) {
      if (data?.description) {
        setDescription(nextDesc);
      } else {
        setDescription("");
      }
    }
    setFileCategory(nextCategory);
    setFileSubCategory(nextSubCategory);
    setFileYear(nextYear);
    setFileMonth(nextMonth);
    setFileMonthYear(computeMonthYearDate(nextMonth, nextYear));
    setBaselineCategory(nextCategory);
    setBaselineSubCategory(nextSubCategory);
    setBaselineYear(nextYear);
    setBaselineMonth(nextMonth);
    setLastModified(
      (data as any)?.lastModified ??
        (data as any)?.updatedAt ??
        data?.createdAt ??
        "",
    );
    setFilteredCategories(categoryOptions);
  }, [data, hasLocalDescriptionOverride]);

  useEffect(() => {
    setFilteredCategories(categoryOptions);
  }, [categoryOptions]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const isOutside =
        categoryAccordionRef.current &&
        !categoryAccordionRef.current.contains(event.target as Node);

      if (isOutside && (showCategories || showSubcategories)) {
        setShowCategories(false);
        setShowSubcategories(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showCategories, showSubcategories]);

  useEffect(() => {
    if (!metadataErrorMessage && !descriptionSuccessMessage) return;

    const timeout = setTimeout(() => {
      setMetadataErrorMessage("");
      setDescriptionSuccessMessage("");
    }, 3000);

    return () => clearTimeout(timeout);
  }, [metadataErrorMessage, descriptionSuccessMessage]);

  useEffect(() => {
    if (!fileCategory || !fileSubCategory) return;

    const categoryChanged =
      fileCategory !== baselineCategory ||
      fileSubCategory !== baselineSubCategory;

    if (categoryChanged) {
      handleMetadataSave("category");
    }
  }, [fileCategory, fileSubCategory]);

  useEffect(() => {
    if (!fileMonth || !fileYear) return;

    const dateChanged =
      String(fileMonth) !== String(baselineMonth) ||
      String(fileYear) !== String(baselineYear);

    if (dateChanged) {
      handleMetadataSave("date");
    }
  }, [fileMonth, fileYear]);

  return {
    open,
    setOpen,
    description,
    setDescription,
    isUpdated,
    restoreMessage,
    setRestoreMessage,
    descriptionSuccessMessage,
    safeDecode,
    truncateText,
    formatDate,
    canEdit,
    isGxmAdmin,
    isEditMode,
    activeTab,
    setActiveTab,
    fileCategory,
    fileSubCategory,
    fileYear,
    fileMonth,
    fileMonthYear,
    lastModified,
    saving,
    showCategories,
    setShowCategories,
    showSubcategories,
    setShowSubcategories,
    filteredCategories,
    categoryAccordionRef,
    categoryName,
    subCategoryName,
    selectedCategory,
    months,
    yearOptions,
    hasChanges,
    hasDescriptionChange,
    hasMetadataChange,
    sidebarVisible,
    setSidebarVisible,
    isControlled,
    searchCategory,
    handleCategorySelect,
    handleSubCategorySelect,
    handleDescriptionSave,
    handleMetadataSave,
    handleDescriptionDiscard,
    handleMetadataDiscard,
    handleMonthYearChange,
    handleViewInBrowser,
    handleDownloadFile,
    openEditMode,
    metadataErrorMessage,
    setMetadataErrorMessage,
    categorySaving,
    dateSaving,
  };
}

export default useDocumentDescription;
